import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Dfns from 'date-fns/fp';
import { MdRefresh } from 'react-icons/md';
import {
  BrandHealthEntry,
  EnrichedGqvInfo,
  GqvChartRow,
  GqvMapRow
} from '@blisspointmedia/bpm-types/dist/BrandHealthMetrics';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { Intent, Placement, Size } from '@Components/designConstants';
import { DatePicker } from '../../Components/DatePicker/DatePicker';
import { Dropdown } from '../../Components/Dropdown/Dropdown';
import { Page } from '../../Components/Page/Page';
import { useSetError } from '../../redux/modals';
import { useExperimentFlag } from '../../utils/experiment-context';
import { MetricsLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useMap } from '../../utils/hooks/useData';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { DateRange } from '../../utils/date-utils';
import './BrandHealthMetrics.scss';
import { NAVS, TabKey } from './brand-health-metrics-constants';
import { formatDate } from './BrandMetricsContent';
import { GoogleBrandHealth } from './GoogleBrandHealth';
import { YouGovBrandHealth } from './YouGovBrandHealth';
import {
  BrandHealthChartFilters,
  useBrandHealthAdvertiserInfo,
  useBrandHealthChartData
} from './use-brand-health-data';

const NO_DATA: BrandHealthEntry[] = [];

export const BrandHealthMetrics = () => {
  const { cid } = useCompanyInfo();
  const shouldEnableBrandHealthMetrics =
    useExperimentFlag('enableBrandHealthMetrics') || TEST_COMPANIES.includes(cid);

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'brand-health-metrics',
    defaultKey: shouldEnableBrandHealthMetrics ? TabKey.YOU_GOV : TabKey.GOOGLE,
    navs: NAVS.map(nav => nav.key)
  });

  const setError = useSetError();
  const youGovEnabled = Boolean(shouldEnableBrandHealthMetrics) && tab === TabKey.YOU_GOV;
  const { data: advertiserInfo, error: advertiserInfoError } = useBrandHealthAdvertiserInfo(
    cid,
    youGovEnabled
  );
  const focalAdvertiser = advertiserInfo?.focalAdvertiser ?? '';
  const nonFocalAdvertisers = useMemo(
    () => advertiserInfo?.nonFocalAdvertisers ?? [],
    [advertiserInfo]
  );
  const minDate = advertiserInfo?.minDate ?? '';
  const maxDate = advertiserInfo?.maxDate ?? '';
  // The regions this advertiser's data actually contains, from getAdvertiserInfo. Sent by the
  // lambda as labels rather than a country code, so there is no catalog to keep in step here and a
  // new country needs no change to this page. Empty until the response arrives, and empty for a
  // response cached before the field existed (`regions` is optional on AdvertiserInfo, bpm-types
  // 4.5.0) — both mean "offer no region filter".
  const regions = useMemo(() => advertiserInfo?.regions ?? [], [advertiserInfo]);
  const [dateRange, setDateRange] = useState<DateRange>();
  const [movingAvgDays, setMovingAvgDays] = useState<string>('14');
  const [ageMap, setAgeMap] = useMap<string, boolean>({
    'Age-All': true,
    '18-34': false,
    '35-49': false,
    '50+': false
  });
  const ageGroup = useMemo(() => {
    if (ageMap['Age-All']) {
      return '18-34,35-49,50+';
    }
    return Object.keys(ageMap)
      .filter(age => ageMap[age])
      .join(',');
  }, [ageMap]);
  const [genderMap, setGenderMap] = useMap<string, boolean>({
    'Gender-All': true,
    Male: false,
    Female: false
  });
  const genderGroup = useMemo(() => {
    if (genderMap['Gender-All']) {
      return 'Male,Female';
    }
    return Object.keys(genderMap)
      .filter(gender => genderMap[gender])
      .join(',');
  }, [genderMap]);
  // Seeded with only "All": the individual labels come from the advertiser, so they are not
  // knowable until getAdvertiserInfo returns. The effect below fills them in once they are.
  const [regionMap, setRegionMap] = useMap<string, boolean>({ 'Region-All': true });
  const regionGroup = useMemo(() => {
    // Empty, not a list. getBrandHealthInfo drops its region predicate for an empty group, which is
    // what "every region this advertiser has" means. Enumerating labels is what broke Canada: the
    // hardcoded US set matched none of its rows. Empty also covers the window where `regions` is
    // not known yet, where any guess would be the same bug.
    if (regionMap['Region-All']) {
      return '';
    }
    // Filtered through `regions` rather than Object.keys(regionMap), so neither the 'Region-All'
    // key nor a label left over from a previously viewed advertiser can reach the query.
    return regions.filter(region => regionMap[region]).join(',');
  }, [regionMap, regions]);
  const [incomeMap, setIncomeMap] = useMap<string, boolean>({
    All: true,
    'Under 40K': false /* deprecated */,
    '40K - 80K': false /* deprecated */,
    '80K - 120K': false /* deprecated */,
    'Over 120K': false /* deprecated */,
    'Under 25K': false,
    '25K - 100K': false,
    'Over 100K': false
  });
  const incomeGroup = useMemo(
    () =>
      Object.keys(incomeMap)
        .filter(income => incomeMap[income])
        .join(','),
    [incomeMap]
  );
  const [dateInterval, setDateInterval] = useState<string>('day');

  const movingAvgOptions = useMemo(() => {
    const options = [
      { value: '1', label: 'None' },
      { value: '7', label: '7 days' },
      { value: '14', label: '14 days' },
      { value: '30', label: '30 days' },
      { value: '60', label: '60 days' }
    ];

    if (cid === 'liquidiv') {
      options.push({ value: '84', label: '84 days' });
    }

    return options;
  }, [cid]);

  const [appliedFilters, setAppliedFilters] = useState<Record<string, boolean | undefined>>({
    'Age-All': true,
    '18-34': false,
    '35-49': false,
    '50+': false,
    'Gender-All': true,
    Male: false,
    Female: false,
    // Region-All only: the individual region keys are added by the effect below, once the
    // advertiser's regions are known. FiltersPanel's areFiltersApplied compares appliedFilters and
    // selectedFilters by key COUNT before comparing values, so this object has to carry the same
    // key set as regionMap does -- and regionMap is now seeded from the advertiser rather than
    // hardcoded. Leaving the four US keys here reported "filters not applied" for every advertiser
    // whose region count is not four, which is every non-US one.
    'Region-All': true,
    All: true,
    'Under 40K': false /* deprecated */,
    '40K - 80K': false /* deprecated */,
    '80K - 120K': false /* deprecated */,
    'Over 120K': false /* deprecated */,
    'Under 25K': false,
    '25K - 100K': false,
    'Over 100K': false
  });

  const [fetchingData, setFetchingData] = useState<boolean>(true);
  // The filters of the chart on screen. Set from the panel when a fetch is requested, not on every
  // checkbox change, so the chart follows the Apply button as before.
  const [chartFilters, setChartFilters] = useState<BrandHealthChartFilters>();
  const { data: chartData, error: chartError } = useBrandHealthChartData(
    cid,
    chartFilters,
    youGovEnabled
  );
  // Held back until the advertiser info is in too, so the chart never draws without its labels.
  // The two requests still run in parallel.
  const data = (advertiserInfo && chartData) || NO_DATA;

  const [dateRangeGoogle, setDateRangeGoogle] = useState<DateRange>();
  const [movingAvgDaysGqv, setMovingAvgDaysGqv] = useState<string>('14');
  const [gqvChartData, setGqvChartData] = useState<GqvChartRow[]>([]);
  const [gqvMapData, setGqvMapData] = useState<GqvMapRow[]>([]);
  const [brandTerms, setBrandTerms] = useState<string[]>([]);
  const [noGoogleData, setNoGoogleData] = useState<boolean>(false);
  const noGoogleDataModalShownRef = useRef<boolean>(false);
  const [googleDataLoading, setGoogleDataLoading] = useState<boolean>(true);
  const googleDataFetchStartedRef = useRef<boolean>(false);

  // Give each of the advertiser's regions a checkbox once we know them. Until then the panel offers
  // only "All", which is correct rather than merely safe: "All" sends no region filter at all.
  //
  // appliedFilters is seeded in the same place and from the same list, because FiltersPanel compares
  // it against selectedFilters by key count first. The two have to gain their region keys together
  // or every non-US advertiser reads as "filters not applied" from the first render.
  useEffect(() => {
    setRegionMap('Region-All', true);
    for (const region of regions) {
      setRegionMap(region, false);
    }
    setAppliedFilters(previous => ({
      ...previous,
      'Region-All': true,
      ...Object.fromEntries(regions.map(region => [region, false]))
    }));
  }, [regions, setRegionMap]);

  useEffect(() => {
    if (advertiserInfoError) {
      setError({
        message: advertiserInfoError.message,
        reportError: advertiserInfoError
      });
    }
  }, [advertiserInfoError, setError]);

  useEffect(() => {
    if (chartError) {
      setError({
        message: chartError.message,
        reportError: chartError
      });
    }
  }, [chartError, setError]);

  useEffect(() => {
    if (tab !== TabKey.GOOGLE || googleDataFetchStartedRef.current) {
      return;
    }
    googleDataFetchStartedRef.current = true;
    const fetchGoogleData = async () => {
      try {
        const res = await MetricsLambdaFetch('/getMetaAndGqvData', {
          params: {
            company: cid
          }
        });
        const parsedRes = await awaitJSON<EnrichedGqvInfo>(res);
        if (!parsedRes?.chartData?.length) {
          setNoGoogleData(true);
          return;
        }
        setBrandTerms(parsedRes.gqvMetaData.brands.map((brand: string) => brand.trim()));
        setGqvChartData(parsedRes.chartData);
        setGqvMapData(parsedRes.mapData);
        setDateRangeGoogle({ start: parsedRes.minDate, end: parsedRes.maxDate });
      } catch (e) {
        setError({
          message: e.message,
          reportError: e
        });
      } finally {
        setGoogleDataLoading(false);
      }
    };
    fetchGoogleData();
  }, [cid, setError, tab]);

  useEffect(() => {
    if (noGoogleData && tab === TabKey.GOOGLE && !noGoogleDataModalShownRef.current) {
      noGoogleDataModalShownRef.current = true;
      setError({
        title: 'No Data Available',
        message: `No Brand Health Google data is available for ${cid}.`,
        intent: Intent.WARNING
      });
    }
  }, [noGoogleData, tab, cid, setError]);

  useEffect(() => {
    if (!shouldEnableBrandHealthMetrics) {
      return;
    }
    setFetchingData(true);
  }, [movingAvgDays, dateInterval, shouldEnableBrandHealthMetrics]);

  useEffect(() => {
    if (!shouldEnableBrandHealthMetrics || !fetchingData) {
      return;
    }
    setFetchingData(false);
    // Merged into the previous value rather than replacing it. On the first load this effect
    // and the region-seeding effect above flush in the same batch, and this one's `regionMap`
    // closure predates the seeding -- a literal object here would drop the region keys the
    // seeding effect just added, leaving appliedFilters short of regionMap by exactly those
    // keys and FiltersPanel's key-count check reading "filters not applied" forever.
    setAppliedFilters(previous => ({
      ...previous,
      ...ageMap,
      ...genderMap,
      ...regionMap,
      ...incomeMap
    }));
    // No advertiser or date parameters: the lambda resolves them from the cid, so this request
    // does not wait for getAdvertiserInfo.
    setChartFilters({
      rollingAvgDaysMinusOne: Number.parseInt(movingAvgDays, 10) - 1,
      ageGroup,
      genderGroup,
      regionGroup,
      incomeGroup,
      dateInterval
    });
  }, [
    ageGroup,
    genderGroup,
    incomeGroup,
    regionGroup,
    movingAvgDays,
    fetchingData,
    ageMap,
    genderMap,
    regionMap,
    incomeMap,
    dateInterval,
    shouldEnableBrandHealthMetrics
  ]);

  const [updatingDateRange, setUpdatingDateRange] = useState<boolean>(true);

  useEffect(() => {
    setUpdatingDateRange(true);
  }, [dateInterval]);

  useEffect(() => {
    // If statement covers initial load of the page.
    if (!dateRange && minDate && maxDate && updatingDateRange) {
      setUpdatingDateRange(false);

      const maxDateObject = Dfns.parseISO(maxDate);
      const tempStart = Dfns.format(
        'yyyy-MM-dd',
        maxDateObject.setDate(maxDateObject.getDate() - 180)
      );

      setDateRange({
        start: tempStart < minDate ? minDate : tempStart,
        end: maxDate
      });
    } else if (dateRange && dateInterval === 'week' && updatingDateRange) {
      setUpdatingDateRange(false);

      let previousMondayStart = Dfns.parseISO(dateRange.start);
      previousMondayStart.setDate(
        previousMondayStart.getDate() - ((previousMondayStart.getDay() + 6) % 7)
      );
      // Check if the start date is before the min date. If so, set to following week.
      if (previousMondayStart < Dfns.parseISO(minDate)) {
        previousMondayStart = Dfns.parseISO(minDate);
        previousMondayStart.setDate(
          previousMondayStart.getDate() + (8 - previousMondayStart.getDay())
        );
      }

      let previousMondayEnd = Dfns.parseISO(dateRange.end);
      previousMondayEnd.setDate(
        previousMondayEnd.getDate() - ((previousMondayEnd.getDay() + 6) % 7)
      );
      // Check if the end date is before the min date. If so, set to following week.
      if (previousMondayEnd < Dfns.parseISO(minDate)) {
        previousMondayEnd = Dfns.parseISO(minDate);
        previousMondayEnd.setDate(previousMondayEnd.getDate() + (8 - previousMondayEnd.getDay()));
      }

      setDateRange({
        start: Dfns.format('yyyy-MM-dd', previousMondayStart),
        end: Dfns.format('yyyy-MM-dd', previousMondayEnd)
      });
    } else if (dateRange && dateInterval === 'month' && updatingDateRange) {
      setUpdatingDateRange(false);

      const firstOfMonthStart = Dfns.parseISO(dateRange.start);
      firstOfMonthStart.setDate(1);
      // Check if the start date is before the min date. If so, set to following month.
      if (firstOfMonthStart < Dfns.parseISO(minDate)) {
        firstOfMonthStart.setMonth(firstOfMonthStart.getMonth() + 1);
      }

      const firstOfMonthEnd = Dfns.parseISO(dateRange.end);
      firstOfMonthEnd.setDate(1);
      // Check if the end date is before the min date. If so, set to following month.
      if (firstOfMonthEnd < Dfns.parseISO(minDate)) {
        firstOfMonthEnd.setMonth(firstOfMonthEnd.getMonth() + 1);
      }

      setDateRange({
        start: Dfns.format('yyyy-MM-dd', firstOfMonthStart),
        end: Dfns.format('yyyy-MM-dd', firstOfMonthEnd)
      });
    } else if (dateRange && dateInterval === 'quarter' && updatingDateRange) {
      setUpdatingDateRange(false);

      const quarterStart = Dfns.parseISO(dateRange.start);
      const quarterStartMonth = quarterStart.getMonth();
      if (quarterStartMonth < 3) {
        quarterStart.setMonth(0);
        quarterStart.setDate(1);
        // Check if the start date is before the min date. If so, set to following quarter.
        if (quarterStart < Dfns.parseISO(minDate)) {
          quarterStart.setMonth(3);
        }
      } else if (quarterStartMonth < 6) {
        quarterStart.setMonth(3);
        quarterStart.setDate(1);
        // Check if the start date is before the min date. If so, set to following quarter.
        if (quarterStart < Dfns.parseISO(minDate)) {
          quarterStart.setMonth(6);
        }
      } else if (quarterStartMonth < 9) {
        quarterStart.setMonth(6);
        quarterStart.setDate(1);
        // Check if the start date is before the min date. If so, set to following quarter.
        if (quarterStart < Dfns.parseISO(minDate)) {
          quarterStart.setMonth(9);
        }
      } else {
        quarterStart.setMonth(9);
        quarterStart.setDate(1);
        // Check if the start date is before the min date. If so, set to following quarter.
        if (quarterStart < Dfns.parseISO(minDate)) {
          quarterStart.setMonth(0);
          quarterStart.setFullYear(quarterStart.getFullYear() + 1);
        }
      }

      const quarterEnd = Dfns.parseISO(dateRange.end);
      const quarterEndMonth = quarterEnd.getMonth();
      if (quarterEndMonth < 3) {
        quarterEnd.setMonth(0);
        quarterEnd.setDate(1);
        // Check if the end date is before the min date. If so, set to following quarter.
        if (quarterEnd < Dfns.parseISO(minDate)) {
          quarterEnd.setMonth(3);
        }
      } else if (quarterEndMonth < 6) {
        quarterEnd.setMonth(3);
        quarterEnd.setDate(1);
        // Check if the end date is before the min date. If so, set to following quarter.
        if (quarterEnd < Dfns.parseISO(minDate)) {
          quarterEnd.setMonth(6);
        }
      } else if (quarterEndMonth < 9) {
        quarterEnd.setMonth(6);
        quarterEnd.setDate(1);
        // Check if the end date is before the min date. If so, set to following quarter.
        if (quarterEnd < Dfns.parseISO(minDate)) {
          quarterEnd.setMonth(9);
        }
      } else {
        quarterEnd.setMonth(9);
        quarterEnd.setDate(1);
        // Check if the end date is before the min date. If so, set to following quarter.
        if (quarterEnd < Dfns.parseISO(minDate)) {
          quarterEnd.setMonth(0);
          quarterEnd.setFullYear(quarterEnd.getFullYear() + 1);
        }
      }

      setDateRange({
        start: Dfns.format('yyyy-MM-dd', quarterStart),
        end: Dfns.format('yyyy-MM-dd', quarterEnd)
      });
    } else if (dateRange && dateInterval === 'day' && updatingDateRange) {
      setUpdatingDateRange(false);
    }
  }, [dateInterval, dateRange, maxDate, minDate, updatingDateRange]);

  const calendarSelectionDropdownOptions = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: 'Quarter' }
  ];

  const isDayBlocked = useCallback(
    (date: string) => {
      let checkForDateInterval = false;
      if (dateInterval === 'week') {
        checkForDateInterval = !Dfns.isMonday(Dfns.parseISO(date));
      } else if (dateInterval === 'month') {
        checkForDateInterval = !Dfns.isFirstDayOfMonth(Dfns.parseISO(date));
      } else if (dateInterval === 'quarter') {
        checkForDateInterval = !(
          date.substring(5) === '01-01' ||
          date.substring(5) === '04-01' ||
          date.substring(5) === '07-01' ||
          date.substring(5) === '10-01'
        );
      }

      return date < minDate || date > maxDate || checkForDateInterval;
    },
    [dateInterval, maxDate, minDate]
  );

  const inLoadingState = data.length === 0;
  const enabledNavs = shouldEnableBrandHealthMetrics
    ? NAVS
    : NAVS.filter(nav => nav.key !== TabKey.YOU_GOV);

  return (
    <Page
      title='Brand Health Metrics'
      pageType='Brand Health Metrics'
      navs={enabledNavs}
      selectedNav={tab}
      onNav={key => goToTab(key as TabKey)}
      actions={
        tab === TabKey.YOU_GOV && shouldEnableBrandHealthMetrics
          ? maxDate && (
              <div className='bhm-actions'>
                <div className='last-updated'>
                  <div className='last-updated-icon'>
                    <MdRefresh />
                  </div>
                  <div className='last-updated-text'>{`Data last updated: ${formatDate(maxDate)}`}</div>
                </div>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  disabled={inLoadingState}
                  value={dateInterval}
                  label='Date Interval'
                  options={calendarSelectionDropdownOptions}
                  onChange={setDateInterval}
                />
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  disabled={inLoadingState}
                  label='Moving Average'
                  value={movingAvgDays}
                  options={movingAvgOptions}
                  onChange={setMovingAvgDays}
                />
                <DatePicker
                  range={dateRange}
                  onChange={setDateRange}
                  isDayBlocked={isDayBlocked}
                  maxDate={maxDate}
                />
              </div>
            )
          : tab === TabKey.GOOGLE &&
            dateRangeGoogle && (
              <div className='bhm-actions'>
                <div className='last-updated'>
                  <div className='last-updated-icon'>
                    <MdRefresh />
                  </div>
                  <div className='last-updated-text'>{`Data last updated: ${formatDate(
                    dateRangeGoogle.end
                  )}`}</div>
                </div>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  label='Moving Average'
                  value={movingAvgDaysGqv}
                  options={movingAvgOptions}
                  onChange={setMovingAvgDaysGqv}
                />
                <DatePicker
                  range={dateRangeGoogle}
                  onChange={setDateRangeGoogle}
                  removeHeaderText
                />
              </div>
            )
      }
    >
      {tab === TabKey.YOU_GOV && shouldEnableBrandHealthMetrics && (
        <YouGovBrandHealth
          focalAdvertiser={focalAdvertiser}
          nonFocalAdvertisers={nonFocalAdvertisers}
          data={data}
          dateRange={dateRange}
          ageMap={ageMap}
          setAgeMap={setAgeMap}
          genderMap={genderMap}
          setGenderMap={setGenderMap}
          regionMap={regionMap}
          regionLabels={regions}
          setRegionMap={setRegionMap}
          incomeMap={incomeMap}
          setIncomeMap={setIncomeMap}
          setFetchingData={setFetchingData}
          appliedFilters={appliedFilters}
          inLoadingState={inLoadingState}
        />
      )}
      {tab === TabKey.GOOGLE && (
        <GoogleBrandHealth
          chartData={gqvChartData}
          mapData={gqvMapData}
          dateRange={dateRangeGoogle}
          uniqueLabels={['Brand Terms', 'Category Terms']}
          uniqueLabelsToSearchTerms={
            new Map<string, string[]>([
              ['Brand Terms', brandTerms],
              ['Category Terms', ['From Google PASCAT taxonomy']]
            ])
          }
          movingAvgDaysGqv={movingAvgDaysGqv}
          isLoading={googleDataLoading}
        />
      )}
    </Page>
  );
};
